import { restartableTask } from 'ember-concurrency';
import { IDateFilter } from 'volta/_pods/protected/bom/controller';
// @ts-ignore
import BomPartCodeCell from 'volta/components/bom/tree-table/part-code-cell';
// @ts-ignore
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import { ResourceName as BomNodeResourceName } from 'volta/models/bom-node';
import { ResourceName } from 'volta/models/sku';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type BomNode from 'volta/models/bom-node';
import type SkuModel from 'volta/models/sku';
import type LabelService from 'volta/services/label-service';
import type StoreService from 'volta/services/store';
import type { DocWithData } from 'volta/utils/api/jsonapi-types';

interface IArgs {
  skuId: string;
  sku: SkuModel;
  baseSkuPath?: string;
}

export default class SkuDescendants extends Component<IArgs> {
  @service store!: StoreService;
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked nodes: BomNode[] = [];
  @tracked sorts = [{ valuePath: 'sku.partCode', isAscending: true }];
  @tracked selectedDescendants: BomNode[] = [];
  @tracked dateFilter: IDateFilter = { startDate: undefined, endDate: undefined };

  resourceName = ResourceName;

  get children() {
    return this.nodes ? this.nodes.filter((n) => n.parentSkuId === this.args.skuId) : [];
  }

  get skuCount() {
    return this.nodes && this.nodes.length
      ? this.nodes.filter((n) => n.sku.id !== this.args.skuId).length
      : 0;
  }

  get cumulatedLeadTime() {
    const { sku } = this.args;
    const lts = this.nodes.map(r => r.cumulatedLeadTime ?? 0) || [];
    if (!lts.length) {
      return sku.leadTime;
    } else {
      return Math.max(...lts) + sku.leadTime;
    }
  }

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get skuNode() {
    return this.nodes && this.nodes.find((n) => n.sku.id === this.args.skuId);
  }

  get maxLevel() {
    const { nodes } = this;
    return nodes && nodes.length ? Math.max(...this.nodes.map((n) => n.level)) : undefined;
  }

  get depth() {
    const { maxLevel } = this;
    return maxLevel ? maxLevel - (this.skuNode ? this.skuNode.level : 0) : undefined;
  }

  @sort('children', 'sortProperties')
  sortedDescendants!: BomNode[];

  @cached
  get activeDescendants() {
    return this.sortedDescendants.filter((node) => node.isActiveAtDate(this.dateFilter));
  }

  get columns(): IBvTableColumn[] {
    const { intl, skuNode } = this;
    const { baseSkuPath } = this.args;

    return [
      {
        columnName: intl.t('source'),
        valuePath: 'sku.warehouse.code',
        isResizable: true,
        isReorderable: false,
        minWidth: 75,
        width: 150,
        isFixed: 'left',
        cellComponent: LabelCell,
        format: (node: BomNode) => {
          const { sku } = node;
          return { ...sku.sourcedFrom, label: sku.sourcedFrom.code };
        }
      },
      {
        columnName: intl.t('part'),
        valuePath: 'sku.partCode',
        sortKey: 'sku.partCode',
        isResizable: true,
        width: 200,
        cellComponent: BomPartCodeCell,
        format: (model: BomNode) => {
          return {
            code: model.sku.partCode,
            description: model.sku.skuDescription,
            link: baseSkuPath || 'protected.plannings.sku',
            linkParam: model.sku.skuId,
            cellClasses: model.sku.isActive ? undefined : 'u-text-strike',
            startDate: model.startDate,
            endDate: model.endDate
          };
        }
      },
      {
        columnName: '',
        valuePath: 'sku.methodology',
        sortKey: 'sku.methodology',
        isResizable: false,
        isReorderable: true,
        width: 35,
        cellComponent: BufferIconCell,
        format: (model: BomNode) => {
          return {
            methodology: model.sku.methodology,
            isProtected: model.sku.isProtected
          };
        }
      },
      {
        columnName: intl.t('acqCodeShort'),
        valuePath: 'sku.acqCode',
        width: 90,
        isReorderable: true,
        isResizable: false,
        cellComponent: LabelCell,
        format: (model: BomNode) => {
          const { acqCode } = model.sku;
          return {
            label: intl.t(`acqCodes.${acqCode || 'UNKNOWN'}`)
          };
        }
      },
      {
        columnName: this.intl.t('leadTimes'),
        valuePath: 'sku.vltDisplay',
        sortKey: 'sku.voltaLeadTime',
        classNames: 'u-text-mono',
        textAlign: 'right',
        isResizable: true,
        isReorderable: true,
        minWidth: 25,
        width: 80
      },
      {
        columnName: this.intl.t('cumulatedLeadTimeShort'),
        valuePath: 'cumulatedLeadTime',
        classNames: 'u-text-mono',
        textAlign: 'right',
        isResizable: true,
        isReorderable: true,
        minWidth: 25,
        width: 80
      },
      {
        columnName: intl.t('qty'),
        sortKey: 'qty',
        valuePath: 'qty',
        isReorderable: true,
        isResizable: true,
        width: 80,
        classNames: 'u-text-mono',
        textAlign: 'right',
        format: (model: BomNode) => {
          const qty = skuNode ? (skuNode.qty ? model.qty / skuNode.qty : 0) : model.qty;
          return `${intl.formatNumber(qty, {
            maximumFractionDigits: 10
          })} ${this.labelService.unitMeasure(model.partUnitMeasure)}`;
        }
      }
    ];
  }

  @action
  filterActiveNodes(dateFilter: IDateFilter) {
    this.dateFilter = dateFilter;
  }

  getDescendantsTask = restartableTask(async (skuId: string) => {
    try {
      const response: DocWithData = await this.store.normalGet(`bomNodes/descendants/${skuId}`, {
        include: 'sku'
      });
      const normalizedResponse = normalize(BomNodeResourceName.singular, response) as BomNode[];
      this.nodes = normalizedResponse;
      return this.nodes;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });
}
